/**
 * Monday.com Import Service
 * Transforms parsed Monday.com Excel data into board structure with groups and tasks
 * Supports dynamic column detection and custom field creation
 */
/**
 * Standard field names that map to built-in task properties
 */
const STANDARD_FIELDS = new Set([
    'title',
    'name',
    'status',
    'priority',
    'due date',
    'due_date',
    'assignee',
    'assignees',
    'owner',
    'created',
    'created_at',
    'updated',
    'updated_at',
    'progress',
    'completion',
]);
/**
 * Detect if a column is a standard field
 */
function isStandardField(columnName) {
    const normalized = columnName.toLowerCase().replace(/[_\s-]/g, '');
    for (const field of STANDARD_FIELDS) {
        const normalizedField = field.toLowerCase().replace(/[_\s-]/g, '');
        if (normalized === normalizedField) {
            return true;
        }
    }
    return false;
}
/**
 * Get title from task (try multiple column names)
 */
function extractTitle(task, groupColumnHeaders) {
    const titleCandidates = ['title', 'name', 'task', 'item'];
    for (const candidate of titleCandidates) {
        const header = groupColumnHeaders.find((h) => h.toLowerCase() === candidate);
        if (header && task[header]) {
            return String(task[header]).trim();
        }
    }
    // Fallback: use first non-empty value
    for (const header of groupColumnHeaders) {
        if (task[header]) {
            return String(task[header]).trim();
        }
    }
    return 'Untitled Task';
}
/**
 * Get status from task (try multiple column names)
 */
function extractStatus(task, groupColumnHeaders) {
    const statusCandidates = ['status', 'state', 'stage'];
    for (const candidate of statusCandidates) {
        const header = groupColumnHeaders.find((h) => h.toLowerCase() === candidate);
        if (header && task[header]) {
            return String(task[header]).trim();
        }
    }
    return 'Not Started';
}
/**
 * Get priority from task (try multiple column names)
 */
function extractPriority(task, groupColumnHeaders) {
    const priorityCandidates = ['priority', 'importance', 'level'];
    for (const candidate of priorityCandidates) {
        const header = groupColumnHeaders.find((h) => h.toLowerCase() === candidate);
        if (header && task[header]) {
            return String(task[header]).trim();
        }
    }
    return 'Medium';
}
/**
 * Extract custom fields from task (non-standard fields)
 */
function extractCustomFields(task, groupColumnHeaders) {
    const customFields = {};
    groupColumnHeaders.forEach((header) => {
        if (!isStandardField(header)) {
            const value = task[header];
            if (value !== null && value !== '') {
                customFields[header] = value;
            }
        }
    });
    return customFields;
}
/**
 * Transform Monday.com parsed data into board structure
 */
export async function transformMondayImportData(config) {
    const { parseResult, userId, userName } = config;
    const newColumns = new Set();
    const importedGroups = [];
    let totalTasks = 0;
    // Process each group
    for (const parsedGroup of parseResult.groups) {
        const importedTasks = [];
        for (const task of parsedGroup.tasks) {
            const title = extractTitle(task, parsedGroup.columnHeaders);
            const status = extractStatus(task, parsedGroup.columnHeaders);
            const priority = extractPriority(task, parsedGroup.columnHeaders);
            const customFields = extractCustomFields(task, parsedGroup.columnHeaders);
            // Track new columns
            Object.keys(customFields).forEach((fieldName) => {
                newColumns.add(fieldName);
            });
            importedTasks.push({
                title,
                status,
                priority,
                customFields,
            });
            totalTasks++;
        }
        if (importedTasks.length > 0) {
            importedGroups.push({
                title: parsedGroup.name,
                tasks: importedTasks,
                taskCount: importedTasks.length,
            });
        }
    }
    return {
        board: {
            title: parseResult.boardTitle,
            description: parseResult.boardDescription,
        },
        groups: importedGroups,
        newColumns: Array.from(newColumns),
        taskCount: totalTasks,
        groupCount: importedGroups.length,
    };
}
/**
 * Convert imported data to COWBoard format
 */
export function createBoardFromMonday(importResult, userId, userName) {
    const groups = importResult.groups.map((group, groupIdx) => ({
        id: `group_${Date.now()}_${groupIdx}`,
        title: group.title,
        color: getColorForGroup(groupIdx),
        position: groupIdx,
        tasks: group.tasks.map((task, taskIdx) => ({
            id: `task_${Date.now()}_${groupIdx}_${taskIdx}`,
            title: task.title,
            status: task.status,
            priority: task.priority,
            assigneeIds: [],
            agentIds: [],
            comments: [],
            customFields: task.customFields,
            updatedBy: {
                date: Date.now(),
                userId,
                userAvatar: '',
            },
            automationConfig: {
                autoAssignAgent: false,
                agentTriggers: [],
                escalationRules: [],
                autonomousMode: false,
            },
        })),
        isCollapsed: false,
    }));
    return {
        id: `board_${Date.now()}`,
        title: importResult.board.title,
        description: importResult.board.description,
        isStarred: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: {
            id: userId,
            name: userName,
            avatar: '',
        },
        members: [
            {
                id: userId,
                name: userName,
                avatar: '',
            },
        ],
        groups,
        activities: [
            {
                id: `activity_${Date.now()}`,
                type: 'task_created',
                userId,
                userName,
                timestamp: Date.now(),
            },
        ],
        labels: getDefaultLabels(),
        columnOrder: ['assignee-picker', 'status-picker', 'priority-picker'],
        availableColumns: [
            'assignee-picker',
            'status-picker',
            'priority-picker',
            'date-picker',
            'text-picker',
        ],
        viewType: 'table',
    };
}
/**
 * Get color for group based on position (cycle through nice colors)
 */
function getColorForGroup(index) {
    const colors = [
        '#579bfc', // Blue
        '#9400d3', // Purple
        '#ec5294', // Pink
        '#00c875', // Green
        '#fdab3d', // Orange
        '#e2445c', // Red
        '#0099ff', // Light Blue
        '#6c5ce7', // Indigo
    ];
    return colors[index % colors.length];
}
/**
 * Get default labels for imported board
 */
function getDefaultLabels() {
    return [
        { id: 'status-done', title: 'Done', color: '#00c875', type: 'status' },
        { id: 'status-working', title: 'Working on it', color: '#fdab3d', type: 'status' },
        { id: 'status-stuck', title: 'Stuck', color: '#e2445c', type: 'status' },
        { id: 'status-not-started', title: 'Not Started', color: '#c4c4c4', type: 'status' },
        { id: 'priority-high', title: 'High', color: '#e2445c', type: 'priority' },
        { id: 'priority-medium', title: 'Medium', color: '#fdab3d', type: 'priority' },
        { id: 'priority-low', title: 'Low', color: '#00c875', type: 'priority' },
    ];
}
